import { Municipality } from '@ta-interaktiv/react-municipality-search'
import {
  colors_dm,
  colors_lm,
  getColorKeyEntries,
} from 'Content/CityDatas/sat_images/color_key_entries'
import { ChartFrame } from 'components/ChartFrame'
import { ColorKey } from 'components/ColorKey'
import {
  extent,
  scaleLinear,
  select,
  curveStep,
  line,
  scaleThreshold,
} from 'd3'
import { useEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import { useElementSize } from 'usehooks-ts'
const histdata_ch = require('../../Content/Histograms/CH.json')

const colorThresholds = [0, 54.7383, 60.90139, 66.27568, 72.64528, 100]

const chartHeight = window.innerWidth < 712 ? 350 : 400
const Histogram = ({
  labels,
  gdeData,
  lang,
  colorMode,
}: {
  labels: any
  gdeData: Municipality
  lang: 'de' | 'fr'
  colorMode: 'dark' | 'light' | undefined
}) => {
  const [histData, setHistData] = useState<any>()
  const [elementRef, elementSize] = useElementSize()
  const [yTicks, setYTicks] = useState<number[]>([])

  let colors = colorMode === 'dark' ? colors_dm : colors_lm

  // colors.unshift(colors[0])

  function fetchHistData(nr: number) {
    fetch(
      'https://interaktiv.tagesanzeiger.ch/2024/SozIndex/histo/' + nr + '.json'
    )
      .then((a) => a.json())
      .then((a) => {
        setHistData(a)
      })
  }

  useEffect(() => {
    fetchHistData(gdeData.GDENR)
  }, [gdeData])

  // useEffect(() => {
  //   fetchHistData(gdeData.GDENR)
  // }, [])

  const charArea = useMemo(() => {
    return {
      top: 40,
      right: elementSize.width,
      bottom: chartHeight - 50,
      left: 0,
    }
  }, [elementSize.width])

  const svgRef = useRef<SVGSVGElement>(null)

  var x = useMemo(() => {
    return scaleLinear().range([0, charArea.right]).domain([0, 100])
  }, [charArea])

  const color = scaleThreshold(colorThresholds, [colors[0], ...colors])

  var y = useMemo(() => {
    if (!histData) {
      return scaleLinear()
        .range([charArea.top, charArea.bottom])
        .domain([0, 10])
    }
    var ch_extent = extent<number>(histData.Anteil) as [number, number]
    var gde_extent = extent<number>(histData.Anteil) as [number, number]
    var max = Math.max(ch_extent[1], gde_extent[1])
    var tick_step = max > 0.3 ? 0.1 : 0.05

    max = Math.max(0.2, Math.ceil(max / tick_step) * tick_step)

    const yTicks = []

    tick_step = max > 0.3 ? 0.1 : 0.05

    for (let i = 0; i <= max; i += tick_step) {
      yTicks.push(i)
    }
    setYTicks(yTicks)
    return scaleLinear().range([charArea.top, charArea.bottom]).domain([0, max])
  }, [charArea, histData])

  var a = useMemo(() => {
    return line()
      .x((p: any, i) => x(i) * 5 + (x(2) * 5 - x(1) * 5) / 2)
      .y((p: any) => charArea.bottom + 40 - y(p))
      .curve(curveStep)
  }, [charArea, x, y])

  useEffect(() => {
    if (!histData || !svgRef.current) return
    var svg = select(svgRef.current as any)

    var g = svg.select('.gde').html('')

    const splitbars: any = []
    g.selectAll('.first')
      .data(histData.Anteil)
      .enter()

      .append('rect')
      .attr('class', 'first')
      .attr('x', (d: any, i) => {
        return (x(i) * 5) as number
      })
      .attr('y', (d: any) => charArea.bottom + 40 - y(d))
      .attr('width', (d: any) => x(2) * 5 - x(1) * 5 + 1)
      .attr('height', (d: any) => y(d) - 40)
      .style('fill', 'var(--blue-bright-1')
      .style('fill', (d, i) => {
        if (color(i * 5) !== color((i + 1) * 5)) {
          splitbars.push({ d, x: i * 5, color: color(i * 5) })
        }
        return color((i + 1) * 5)
      })

    g.selectAll('.second')
      .data(splitbars)
      .enter()
      .append('rect')
      .attr('class', 'second')
      .attr('x', (d: any, i) => (x(d.x) - 0.5) as number)
      .attr('y', (d: any) => {
        return charArea.bottom + 40 - y(d.d)
      })
      .attr('width', (d: any) => {
        const nextHigherThreshold = colorThresholds
          .filter((f) => f > d.x)
          .sort((a, b) => a - b)[0]

        var w1 = x(nextHigherThreshold) - x(d.x)
        var w2 = x(d.x + 5) - x(nextHigherThreshold)
        return w1 > w2 ? x(2) * 5 - x(1) * 5 + 1 : 0
      })
      .attr('fill', (d: any, i) => {
        return color(d.x)
      })
      .attr('height', (d: any) => y(d.d) - 40)
      .attr('opacity', 1)

    svg
      .select('.ch')
      .html('')
      .append('path')
      .attr('d', a(histdata_ch.Anteil))
      .attr('fill', 'none')
      .attr('fill', 'url(#diagonalHatch)')
      .attr('stroke-width', 1.5)
      .attr('stroke-linejoin', 'round')
      .attr('stroke-linecap', 'round')
      // .attr("stroke-dasharray", "1,4")
      .attr('stroke', 'var(--chart-ch-contour)')
    // drawChart()
  }, [charArea, histData, x, y, color, a])

  return (
    <HistogramContainer ref={elementRef}>
      <ChartFrame
        lang={lang}
        title={
          lang === 'fr' ? (
            <>
              {gdeData.NORMGEMEINDE} &mdash; {labels.histo_title}
            </>
          ) : (
            labels.histo_title + ' ' + gdeData.NORMGEMEINDE
          )
        }
      >
        <ColorKey entries={getColorKeyEntries(colorMode, lang) as any}>
          {labels.histo_labels[0]}
        </ColorKey>
        <HistogramSVG
          ref={svgRef}
          width={elementSize.width}
          height={chartHeight}
        >
          <pattern
            id='diagonalHatch'
            patternUnits='userSpaceOnUse'
            width='4'
            height='8'
            patternTransform='rotate(-45 2 2)'
          >
            <path
              d='M -1,2 l 6,0'
              stroke='var(--hatch-color)'
              strokeWidth='1'
            />
          </pattern>
      
          <g className='yticks'>
            {yTicks.map((tick, i) => {
              return (
                <g
                key={"ytick_"+i}
                  transform={`translate(${0},${
                    charArea.bottom - y(tick) + 40
                  })`}
                >
                  <line
                    x1={0}
                    x2={elementSize.width}
                    stroke={'var(--chart-tick-line)'}
                    strokeWidth={1}
                  />
                  {i < yTicks.length - 1 && (
                    <text y={-8} fill={'var(--chart-tick)'}>
                      {roundTo(tick, 2) * 100}%
                    </text>
                  )}
                </g>
              )
            })}
          </g>
          <g className='gde'></g>
          <g className='ch'></g>
          <g transform={`translate(${0},${charArea.bottom - y(0) + 40})`}>
            <line
              x1={0}
              x2={elementSize.width}
              stroke={'var(--chart-zero-line)'}
              strokeWidth={3}
            />
          </g>
          <g className='xAxisLabel'>
            <text
              x={charArea.left}
              y={charArea.bottom + 43}
              fill={'var(--chart-axis-title)'}
              fontWeight={700}
              style={{ fontSize: '16px' }}
            >
              {labels.histo_labels[1]}
            </text>
            <text
              x={charArea.left}
              y={charArea.bottom + 20}
              fill={'var(--chart-axis-title)'}
            >
              <tspan>&larr;</tspan> {labels.poor}
            </text>
            <text
              textAnchor='end'
              x={charArea.right}
              y={charArea.bottom + 20}
              fill={'var(--chart-axis-title)'}
            >
              {labels.rich} <tspan>&rarr;</tspan>
            </text>
          </g>

          <g className='yAxisLabel'>
            <text
              x={charArea.left}
              y={charArea.top - 8}
              fill={'var(--chart-axis-title)'}
            >
              {labels.histo_labels[2]}
            </text>
          </g>
        </HistogramSVG>
      </ChartFrame>
    </HistogramContainer>
  )
}

function roundTo(n: number, digits: number) {
  if (digits === undefined) {
    digits = 0
  }

  var multiplicator = Math.pow(10, digits)
  n = parseFloat((n * multiplicator).toFixed(11))
  var test = Math.round(n) / multiplicator
  return +test.toFixed(digits)
}

const HistogramContainer = styled.div`
  margin-left: auto;
  margin-right: auto;
  text-align: center;
  max-width: 900px;
  padding-bottom: 15px;

  font-family: var(--font-sans-serif);
`
const HistogramSVG = styled.svg`
  /* background-color: #f6f6f6; */

  .yticks {
    text {
      fill: var(--chart-tick);
      font-size: 12px;
    }
  }
  .xAxisLabel {
    text {
      tspan {
        font-weight: 700;
        font-size: 20px;
      }
      font-size: 15px;
    }
  }
  .yAxisLabel {
    text {
      font-size: 15px;
    }
  }
`

export default Histogram
